<script>
import { useVuelidate } from '@vuelidate/core';
import { getFieldVariables } from '../../helpers/automationUtils';
import dynamicField from '../dynamicField/Index.vue';
import SidebarHeader from './SidebarHeader.vue';

export default {
  components: {
    DynamicField: dynamicField,
    SidebarHeader,
  },
  props: {
    outputMapping: {
      type: Object,
      default: () => {},
    },
    outputMappingFields: {
      type: Array,
      default: () => [],
    },
    // eslint-disable-next-line vue/no-unused-properties
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      updatedOutputMapping: this.outputMapping,
    };
  },
  watch: {
    outputMapping: {
      handler(value) {
        this.updatedOutputMapping = value;
      },
      immediate: true,
    },
  },
  validations() {
    return {
      updatedOutputMapping: {
        duplicate: (value, { outputMappingFields, variables }) => {
          const inheritedVariables = variables.map(({ name }) => name);
          const outputMappingVariables = this.getVariables(
            value,
            outputMappingFields
          );
          const allVariables = [
            ...inheritedVariables,
            ...outputMappingVariables,
          ];
          const duplicateVariables = allVariables
            .filter(v => v)
            .filter((item, i, items) => items.indexOf(item) !== i);
          return duplicateVariables.length === 0;
        },
      },
    };
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    isVisible(field) {
      if (!field.show_if) return true;

      return field.show_if.every(({ field: fieldName, value }) => {
        const currentValue =
          this.step[fieldName] || this.getField(fieldName)?.default;
        if (!currentValue && currentValue !== 0 && currentValue !== false)
          return false;
        const values = Array.isArray(value) ? value : [value];
        return values.some(v => {
          if (v.includes('*')) {
            return `${currentValue}`.includes(v.replace(/\*/g, ''));
          }
          return currentValue === v;
        });
      });
    },
    getField(fieldName) {
      return this.outputMappingFields.find(({ name }) => name === fieldName);
    },
    onOutputMappingFieldChange(key, value) {
      const outputMappingBefore = this.updatedOutputMapping ?? {};
      const outputMapping = {
        ...outputMappingBefore,
        [key]: value,
      };
      this.updatedOutputMapping = outputMapping;
      this.v$.updatedOutputMapping.$touch();
      this.$emit('change', outputMapping);
    },
    getVariables(outputMapping, outputMappingFields) {
      const variables = getFieldVariables(outputMapping, outputMappingFields);
      const variableNames = variables.map(({ name }) => name);
      return variableNames;
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div class="output-mapping-fields flex flex-col gap-6">
    <SidebarHeader
      :header="
        $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.OUTPUT_MAPPING.TITLE')
      "
      :sub-header="
        $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.OUTPUT_MAPPING.DESCRIPTION')
      "
    />
    <div
      class="rounded-xl flex flex-col gap-6"
      :class="{ 'output-mapping-error': v$.updatedOutputMapping.$error }"
    >
      <DynamicField
        v-for="(field, index) of outputMappingFields"
        :key="index"
        :field="field"
        :value="updatedOutputMapping[field.name]"
        :visible="isVisible(field)"
        :show-variables-selector="false"
        :disabled="disabled"
        :dirty="dirty"
        @change="value => onOutputMappingFieldChange(field.name, value)"
      />

      <p
        v-if="v$.updatedOutputMapping.$error"
        class="min-w-0 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
      >
        <template v-if="v$.updatedOutputMapping.duplicate.$invalid">
          {{
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.OUTPUT_MAPPING.ERRORS.DUPLICATE'
            )
          }}
        </template>
        <template v-else>
          {{
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.OUTPUT_MAPPING.ERRORS.INVALID'
            )
          }}
        </template>
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.name-input:focus {
  outline-color: #6cb8ff !important;
}

.output-mapping-error {
  @apply p-2 my-2 bg-n-ruby-5 mb-4;
}
</style>
